/**
 * Configuration Editor Composable
 * 
 * Manages Monaco editor lifecycle, view mode state, and synchronization
 * between JSON and form views for configuration editing.
 */

import { ref, watch, nextTick, onUnmounted, markRaw, type Ref } from 'vue'
import { monaco, initializeMonaco } from '@/utils/monaco-setup'
import '@/utils/monacoTemplateJson'
import { useTheme } from './useTheme'
import { useConfigurationStore } from '@/stores/configuration'

export interface ConfigurationEditorOptions {
  initialViewMode?: 'form' | 'json'
  presetId?: string
  tenantId?: string
  sectionKey?: string // Unique identifier for this section (e.g., 'preset:123' or 'tenant')
  isReadonly?: boolean | Ref<boolean>
  onJsonChange?: (value: string) => void
}

export function useConfigurationEditor(options: ConfigurationEditorOptions = {}) {
  // Access global view mode from store
  const configStore = useConfigurationStore()
  
  // View mode state (local, but synced with global)
  const viewMode = ref<'form' | 'json'>(options.initialViewMode || 'form')
  
  // Monaco editor state
  const monacoEditor = ref<monaco.editor.IStandaloneCodeEditor | null>(null)
  const monacoEditorRef = ref<HTMLElement | null>(null)
  const isInitializingEditor = ref(false)
  const lastModeChangeTime = ref(0)
  
  // JSON content state
  const jsonContent = ref('')
  
  // Debounce timeout for Monaco content changes
  let changeTimeout: ReturnType<typeof setTimeout> | null = null
  let contentSizeDisposable: monaco.IDisposable | null = null
  let templateHighlightDecorationIds: string[] = []
  
  // Track if there are pending changes (debounce in progress)
  const hasPendingChanges = ref(false)
  
  // Theme management
  const { isDark } = useTheme()

  const buildConfigurationModelUri = () => {
    const sectionSlug = (options.sectionKey ?? options.tenantId ?? options.presetId ?? 'tenant-config')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'tenant-config'

    return monaco.Uri.parse(`inmemory://config/${sectionSlug}-${Date.now()}.json`)
  }

  const updateTemplateVariableHighlights = () => {
    if (!monacoEditor.value) {
      return
    }

    const model = monacoEditor.value.getModel()
    if (!model) {
      return
    }

    const templateVariablePattern = /\{\{[^{}\r\n]+\}\}/g
    const matches = Array.from(model.getValue().matchAll(templateVariablePattern))

    templateHighlightDecorationIds = monacoEditor.value.deltaDecorations(
      templateHighlightDecorationIds,
      matches.map((match) => {
        const startOffset = match.index ?? 0
        const endOffset = startOffset + match[0].length
        const startPosition = model.getPositionAt(startOffset)
        const endPosition = model.getPositionAt(endOffset)

        return {
          range: new monaco.Range(
            startPosition.lineNumber,
            startPosition.column,
            endPosition.lineNumber,
            endPosition.column
          ),
          options: {
            inlineClassName: 'template-variable-highlight'
          }
        }
      })
    )
  }
  
  /**
   * Initialize Monaco editor
   */
  const initializeMonacoEditor = async () => {
    if (isInitializingEditor.value) return
    
    const container = monacoEditorRef.value
    if (!container) {
      console.warn('[useConfigurationEditor] Monaco container not available')
      return
    }
    
    try {
      isInitializingEditor.value = true

      await initializeMonaco()
      
      // Dispose existing editor if present
      if (monacoEditor.value) {
        await disposeMonacoEditor()
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      
      // Double-check we still want to create an editor
      if (viewMode.value !== 'json') {
        return
      }
      
      // Create the editor and mark as non-reactive to prevent Vue tracking
      // This is CRITICAL - Monaco's internal operations must not be tracked by Vue's reactivity
      // Use unref to get current value of isReadonly whether it's a ref or boolean
      const isReadonlyValue = typeof options.isReadonly === 'object' && options.isReadonly !== null && 'value' in options.isReadonly 
        ? options.isReadonly.value 
        : options.isReadonly
      
      const model = monaco.editor.createModel(
        jsonContent.value,
        'json',
        buildConfigurationModelUri()
      )

      monacoEditor.value = markRaw(monaco.editor.create(container, {
        model,
        theme: isDark.value ? 'vs-dark' : 'vs',
        readOnly: isReadonlyValue,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        automaticLayout: false, // We'll handle layout manually
        fontSize: 13,
        lineNumbers: 'on',
        renderWhitespace: 'selection',
        folding: true,
        bracketPairColorization: { enabled: true },
        quickSuggestions: true,
        formatOnPaste: true,
        formatOnType: true,
        'semanticHighlighting.enabled': true,
        // Disable editor's internal scrollbars - let the page scroll instead
        scrollbar: {
          vertical: 'hidden',
          horizontal: 'hidden',
          handleMouseWheel: false, // Don't capture wheel events
          alwaysConsumeMouseWheel: false // Let wheel events pass through
        }
      }))
      
      // Debounce content changes to avoid performance issues on every keystroke
      monacoEditor.value.onDidChangeModelContent(() => {
        updateTemplateVariableHighlights()

        if (changeTimeout) {
          clearTimeout(changeTimeout)
        }
        
        // Mark that we have pending changes
        hasPendingChanges.value = true
        
        changeTimeout = setTimeout(() => {
          if (monacoEditor.value && options.onJsonChange) {
            const value = monacoEditor.value.getValue()
            jsonContent.value = value
            options.onJsonChange(value)
          }
          changeTimeout = null
          // Clear pending changes flag after save completes
          hasPendingChanges.value = false
        }, 500) // 500ms debounce, matches form editor debounce
      })

      updateTemplateVariableHighlights()
      
      // Auto-height: Update editor height to fit content (no internal scrollbar)
      const updateEditorHeight = () => {
        if (!monacoEditor.value || !container) return
        
        const contentHeight = monacoEditor.value.getContentHeight()
        const lineCount = monacoEditor.value.getModel()?.getLineCount() || 1
        
        // Dynamic minimum height based on content:
        // - Small content (≤5 lines): fit exactly to content with small padding
        // - Medium content (6-10 lines): use content height
        // - Larger content: use content height up to max
        const minHeight = lineCount <= 5 ? contentHeight : 120
        const maxHeight = 1200 // Maximum to prevent excessive height
        
        // Clamp height between min and max
        const newHeight = Math.max(minHeight, Math.min(maxHeight, contentHeight))
        
        // Update container height
        container.style.height = `${newHeight}px`
        
        // Update editor layout to match new container size
        monacoEditor.value.layout()
      }
      
      // Listen for content size changes
      contentSizeDisposable = monacoEditor.value.onDidContentSizeChange(() => {
        updateEditorHeight()
      })
      
      // Initial height update
      setTimeout(() => {
        updateEditorHeight()
      }, 50)
      
      console.log('[useConfigurationEditor] Monaco editor initialized')
    } catch (error) {
      console.error('[useConfigurationEditor] Failed to initialize Monaco editor:', error)
    } finally {
      isInitializingEditor.value = false
    }
  }
  
  /**
   * Dispose Monaco editor safely
   */
  const disposeMonacoEditor = async () => {
    if (!monacoEditor.value || isInitializingEditor.value) return
    
    // Clear any pending change timeout
    if (changeTimeout) {
      clearTimeout(changeTimeout)
      changeTimeout = null
    }
    
    if (contentSizeDisposable) {
      contentSizeDisposable.dispose()
      contentSizeDisposable = null
    }
    
    const editorToDispose = monacoEditor.value
    monacoEditor.value = null
    templateHighlightDecorationIds = []
    
    return new Promise<void>((resolve) => {
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => {
          try {
            const model = editorToDispose.getModel()
            if (model) {
              model.dispose()
            }
            editorToDispose.dispose()
            console.log('[useConfigurationEditor] Monaco editor disposed')
          } catch (error) {
            console.error('[useConfigurationEditor] Error disposing editor:', error)
          }
          resolve()
        }, { timeout: 500 })
      } else {
        setTimeout(() => {
          try {
            const model = editorToDispose.getModel()
            if (model) {
              model.dispose()
            }
            editorToDispose.dispose()
            console.log('[useConfigurationEditor] Monaco editor disposed')
          } catch (error) {
            console.error('[useConfigurationEditor] Error disposing editor:', error)
          }
          resolve()
        }, 100)
      }
    })
  }
  
  /**
   * Set view mode
   */
  const setViewMode = (mode: 'form' | 'json', forceSync = false) => {
    const now = Date.now()
    
    // Prevent rapid successive clicks (unless force sync)
    if (!forceSync && now - lastModeChangeTime.value < 500) {
      return
    }
    lastModeChangeTime.value = now
    
    if (isInitializingEditor.value) return
    
    viewMode.value = mode
  }
  
  /**
   * Update JSON content (from external source like form view)
   */
  const updateJsonContent = (content: string) => {
    jsonContent.value = content
    
    // Update Monaco editor if it exists
    if (monacoEditor.value && viewMode.value === 'json') {
      const currentValue = monacoEditor.value.getValue()
      if (currentValue !== content) {
        monacoEditor.value.setValue(content)
      }
    }
  }
  
  // Watch view mode changes
  watch(viewMode, async (newMode, oldMode) => {
    if (oldMode === 'json' && newMode === 'form') {
      // Dispose editor when switching away from JSON
      setTimeout(async () => {
        await disposeMonacoEditor()
      }, 50)
    } else if (newMode === 'json' && oldMode === 'form') {
      // Initialize editor when switching to JSON
      setTimeout(async () => {
        await nextTick()
        await initializeMonacoEditor()
      }, 50) // Wait for transition to complete
    }
  }, { flush: 'post' })
  
  // Watch theme changes and update Monaco editor
  watch(isDark, (dark) => {
    if (monacoEditor.value) {
      monaco.editor.setTheme(dark ? 'vs-dark' : 'vs')
    }
  })
  
  // Watch force sync counter - when it changes, force ALL sections to match global mode
  // This is the ONLY mechanism for global mode synchronization
  // Every click on a global mode button increments this counter
  watch(() => configStore.globalSectionViewModeForceSync, () => {
    const globalMode = configStore.globalSectionViewMode
    // Always call setViewMode with forceSync=true to bypass debounce check
    // and force all sections to sync, even if they were manually toggled
    setViewMode(globalMode, true)
  })
  
  // Watch viewMode changes and update store to enable automatic global sync
  // When all sections have the same view mode, the global toggle will update automatically
  watch(viewMode, (newMode) => {
    if (options.sectionKey) {
      configStore.updateSectionViewMode(options.sectionKey, newMode)
    }
  })
  
  // Register initial view mode in the store
  if (options.sectionKey) {
    configStore.updateSectionViewMode(options.sectionKey, viewMode.value)
  }
  
  // Initialize Monaco editor if starting in JSON mode
  // This is needed when sections are created/recreated with initialViewMode='json'
  if (viewMode.value === 'json') {
    nextTick(() => {
      initializeMonacoEditor()
    })
  }
  
  // Cleanup on unmount
  onUnmounted(async () => {
    // Clean up section view mode tracking
    if (options.sectionKey) {
      configStore.removeSectionViewMode(options.sectionKey)
    }
    await disposeMonacoEditor()
  })
  
  return {
    // State
    viewMode,
    monacoEditor,
    monacoEditorRef,
    jsonContent,
    isInitializingEditor,
    hasPendingChanges,
    
    // Methods
    setViewMode,
    updateJsonContent,
    initializeMonacoEditor,
    disposeMonacoEditor
  }
}
